import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Star, Phone, Facebook, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { hotelInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import BookingModal from './BookingModal';
import { PAGES, langPath, pageLabel, parsePath } from '../utils/siteNav';
import { GUIDES_NAV_LABEL } from '../i18n/guides/meta';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
  </svg>
);

const WHATSAPP_NUMBER = '38268188816';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const { t, language } = useLanguage();
  const { pathname } = useLocation();
  const isHome = parsePath(pathname).slug === '';
  // Sub-pages have a shorter banner, so the header switches to solid sooner
  const solid = isScrolled || isMobileMenuOpen;

  // Social media links
  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/umapremium/', icon: Facebook },
    { name: 'Instagram', url: 'https://www.instagram.com/hotelumapremium/', icon: Instagram },
    { name: 'TikTok', url: 'https://www.tiktok.com/@umapremium', icon: TikTokIcon },
    { name: 'WhatsApp', url: WHATSAPP_URL, icon: WhatsAppIcon, isWhatsapp: true }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = PAGES.map((p) => ({ name: pageLabel(p, t), href: langPath(language, p.slug), slug: p.slug }));
  navLinks.splice(navLinks.findIndex((l) => l.slug === 'location') + 1, 0, { name: GUIDES_NAV_LABEL[language] || GUIDES_NAV_LABEL.en, href: langPath(language, 'guides'), slug: 'guides' });
  const openBooking = () => { setIsMobileMenuOpen(false); setIsBookingOpen(true); };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-6">
        {/* Logo (text-based — Uma Premium brand) */}
        <Link 
          to={langPath(language)}
          onClick={() => { if (isHome) window.scrollTo({ top: 0, behavior: 'smooth' }); setIsMobileMenuOpen(false); }}
          className="flex items-center gap-3 cursor-pointer flex-shrink-0"
          data-testid="header-logo"
        >
          <div className={`transition-colors duration-300 ${solid ? 'text-amber-700' : 'text-white'}`}>
            <span className="block text-xl xl:text-2xl font-serif font-bold tracking-wide whitespace-nowrap leading-tight">Uma Premium</span>
            <div className="flex items-center gap-1 mt-0.5">
              {[...Array(hotelInfo.rating)].map((_, i) => (
                <Star key={`logo-star-${i}`} className="w-3 h-3 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6" data-testid="header-nav">
          {navLinks.map((link) => (
            <NavLink
              key={link.slug}
              to={link.href}
              className={({ isActive }) => `text-[13px] 2xl:text-sm font-medium tracking-wide whitespace-nowrap transition-colors duration-300 hover:text-amber-500 border-b-2 pb-0.5 ${
                isActive ? 'border-amber-500 text-amber-500' : 'border-transparent'
              } ${solid ? (isActive ? 'text-amber-600' : 'text-slate-700') : 'text-white/90'}`}
              data-testid={`nav-${link.slug}`}
            >
              {link.name}
            </NavLink>
          ))}
        </nav>

        {/* CTA, Social, Language & Phone */}
        <div className="hidden xl:flex items-center gap-3 2xl:gap-4">
          {/* Social Media Icons (wide screens only — the menu needs the room in long languages) */}
          <div className="hidden min-[2200px]:flex items-center gap-2 mr-2">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 hover:text-white ${
                  social.isWhatsapp ? 'hover:bg-[#25D366]' : 'hover:bg-amber-500'
                } ${
                  solid ? 'text-slate-500' : 'text-white/70'
                }`}
                aria-label={social.isWhatsapp ? `Chat with us on WhatsApp` : `Follow us on ${social.name}`}
                data-testid={`header-social-${social.name.toLowerCase()}`}
              >
                <social.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
          <LanguageSwitcher isScrolled={solid} />
          <a
            href={`tel:${hotelInfo.phone}`}
            className={`hidden min-[1800px]:flex items-center gap-2 text-sm whitespace-nowrap transition-colors duration-300 ${
              solid ? 'text-slate-600' : 'text-white/80'
            }`}
          >
            <Phone className="w-4 h-4 flex-shrink-0" />
            {hotelInfo.phone}
          </a>
          <Button
            onClick={openBooking}
            className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="header-book-now"
          >
            {t('nav.bookNow')}
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="xl:hidden flex items-center gap-2">
          <LanguageSwitcher isScrolled={solid} />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`p-2 transition-colors duration-300 ${solid ? 'text-slate-700' : 'text-white'}`}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`xl:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col p-6 gap-4" data-testid="mobile-nav">
          {navLinks.map((link) => (
            <NavLink
              key={link.slug}
              to={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) => `text-left font-medium py-2 border-b border-slate-100 hover:text-amber-600 transition-colors ${isActive ? 'text-amber-600' : 'text-slate-700'}`}
              data-testid={`mobile-nav-${link.slug}`}
            >
              {link.name}
            </NavLink>
          ))}
          <Button
            onClick={openBooking}
            className="bg-amber-700 hover:bg-amber-800 text-white mt-4 rounded-full"
            data-testid="mobile-book-now"
          >
            {t('nav.bookNow')}
          </Button>
          
          {/* Mobile Social Links */}
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-100">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 hover:bg-amber-500 hover:text-white transition-all duration-300"
                aria-label={`Follow us on ${social.name}`}
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </nav>
      </div>
      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </header>
  );
};

export default Header;
